const ORDER_STATUS_LABELS = {
  pending: 'Order Received',
  processing: 'Processing',
  shipped: 'Shipped',
  delivered: 'Delivered',
  completed: 'Completed',
  cancelled: 'Cancelled',
  inquiry: 'Inquiry',
  questions: 'Questions',
};

const PAYMENT_STATUS_LABELS = {
  paid: 'Paid',
  pending_payment: 'Payment Pending',
  refunded: 'Refunded',
  partially_refunded: 'Partially Refunded',
};

export function parseOrderNotesHistory(notesHistory) {
  if (!notesHistory) return [];
  try {
    const parsed = typeof notesHistory === 'string' ? JSON.parse(notesHistory) : notesHistory;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function parseInquiryThread(inquiryThread) {
  if (!inquiryThread) return [];
  try {
    const parsed = typeof inquiryThread === 'string' ? JSON.parse(inquiryThread) : inquiryThread;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function inquiryMessageToTimelineEntry(message) {
  const isCustomer = message.author === 'customer';
  return {
    timestamp: message.created_at,
    type: isCustomer ? 'customer_inquiry' : 'admin_reply',
    message: message.message,
    author: message.author,
    author_email: message.author_email,
    author_name: message.author_name,
    sentViaEmail: !isCustomer,
  };
}

function mergeInquiryThreadIntoEntries(entries, inquiryThread) {
  const thread = parseInquiryThread(inquiryThread);
  if (thread.length === 0) return entries;

  const existingKeys = new Set(
    entries
      .filter((entry) => entry.type === 'customer_inquiry' || entry.type === 'admin_reply')
      .map((entry) => `${entry.type}:${entry.timestamp}:${entry.message}`)
  );

  const merged = [...entries];
  thread.forEach((message) => {
    const entry = inquiryMessageToTimelineEntry(message);
    const key = `${entry.type}:${entry.timestamp}:${entry.message}`;
    if (!existingKeys.has(key)) {
      merged.push(entry);
      existingKeys.add(key);
    }
  });

  return merged;
}

export function formatHistoryStatusLabel(status, kind = 'order') {
  if (!status) return 'Unknown';
  const labels = kind === 'payment' ? PAYMENT_STATUS_LABELS : ORDER_STATUS_LABELS;
  if (labels[status]) return labels[status];
  return String(status)
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function getTimelineEntries(order, notesHistory, {
  customerView = false,
  inquiryThread = null,
  hideConversationEntries = false,
} = {}) {
  let entries = parseOrderNotesHistory(notesHistory);
  entries = mergeInquiryThreadIntoEntries(entries, inquiryThread ?? order?.inquiry_thread);

  if (customerView || hideConversationEntries) {
    entries = entries.filter((note) => {
      if (note.type === 'customer_inquiry' || note.type === 'admin_reply') {
        return false;
      }
      if (customerView) {
        return (
          note.type === 'status_change'
          || note.type === 'refund'
          || note.sentViaEmail === true
        );
      }
      return true;
    });
  }

  if (entries.length === 0 && order?.created_at) {
    entries = [{
      timestamp: order.created_at,
      type: 'status_change',
      order_status: order.order_status || order.status || 'pending',
      payment_status: order.payment_status || 'paid',
      message: 'Order placed.',
      sentViaEmail: false,
      synthetic: true,
    }];
  }

  return entries.slice().sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
}

export function getTimelineEntryTitle(note) {
  if (note.type === 'refund') {
    return note.refundAmountFormatted
      ? `Refund: ${note.refundAmountFormatted}`
      : 'Refund processed';
  }

  if (note.type === 'customer_inquiry') {
    return 'Customer Inquiry';
  }

  if (note.type === 'admin_reply') {
    return `Reply from ${note.author_name || 'Cranford Publications'}`;
  }

  if (note.order_status) {
    return formatHistoryStatusLabel(note.order_status);
  }

  if (note.status) {
    return formatHistoryStatusLabel(note.status);
  }

  return 'Order update';
}

export function getTimelineEntryIcon(note) {
  if (note.type === 'refund') return '💰';
  if (note.type === 'customer_inquiry') return '❓';
  if (note.type === 'admin_reply') return '💬';
  const status = note.order_status || note.status;
  switch (status) {
    case 'pending': return '📥';
    case 'processing': return '⚙️';
    case 'shipped': return '📦';
    case 'delivered': return '✅';
    case 'completed': return '✔️';
    case 'cancelled': return '🚫';
    case 'inquiry': return '💬';
    case 'questions': return '❓';
    default: return '📋';
  }
}
